import * as THREE from 'three';
import type { Renderer } from '@/renderer/Renderer';

/** A resolver maps a mesh (by name/keywords) to a material, or returns null to skip. */
export type MaterialResolver = (mesh: THREE.Mesh, name: string) => THREE.Material | null;

/**
 * Central material registry + factory. Encodes the proven Blender → Three rules:
 *  - baked textures: `flipY = false`, sRGB color space, max anisotropy
 *  - data maps (normal/roughness/ao): linear color space
 *  - second-UV (`uv1`) remap to `uv` for Blender AO/lightmaps
 *  - name-based material resolution (extension point for `s1_`, `diamond_`, …)
 *
 * Phase 01 ships the mechanism + a default normalizer; concrete resolvers and
 * emissive/shader animation are layered on in later phases.
 */
export class MaterialManager {
  readonly maxAnisotropy: number;
  private readonly registry = new Map<string, THREE.Material>();
  private readonly resolvers: MaterialResolver[] = [];

  constructor(renderer: Renderer) {
    this.maxAnisotropy = renderer.maxAnisotropy;
  }

  register(name: string, material: THREE.Material): THREE.Material {
    this.registry.set(name, material);
    return material;
  }

  get(name: string): THREE.Material | undefined {
    return this.registry.get(name);
  }

  list(): Array<[string, THREE.Material]> {
    return [...this.registry.entries()];
  }

  /** Register a name-based resolver (evaluated in insertion order). */
  addResolver(resolver: MaterialResolver): void {
    this.resolvers.push(resolver);
  }

  /** Configure a texture consistently for the baked-architecture workflow. */
  prepareTexture(texture: THREE.Texture, color: boolean): THREE.Texture {
    texture.flipY = false;
    texture.colorSpace = color ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    texture.anisotropy = this.maxAnisotropy;
    texture.needsUpdate = true;
    return texture;
  }

  /** Unlit baked material (lightmapped architectural geometry). */
  createBaked(
    map: THREE.Texture,
    params: THREE.MeshBasicMaterialParameters = {},
  ): THREE.MeshBasicMaterial {
    this.prepareTexture(map, true);
    return new THREE.MeshBasicMaterial({ map, ...params });
  }

  /** Normalize a GLTF-authored material: anisotropy on all maps, sRGB on color maps. */
  normalize(material: THREE.Material): void {
    for (const key of Object.keys(material)) {
      const value = (material as unknown as Record<string, unknown>)[key];
      if (value instanceof THREE.Texture) {
        value.anisotropy = this.maxAnisotropy;
        if (key === 'map' || key === 'emissiveMap') value.colorSpace = THREE.SRGBColorSpace;
        value.needsUpdate = true;
      }
    }
    material.needsUpdate = true;
  }

  /** Apply per-mesh processing: UV remap, resolver override, then normalization. */
  applyToMesh(mesh: THREE.Mesh): void {
    // Blender AO/lightmaps bake into a second UV set — remap uv1 -> uv when uv is absent.
    const uv1 = mesh.geometry.getAttribute('uv1');
    if (uv1 && !mesh.geometry.getAttribute('uv')) {
      mesh.geometry.setAttribute('uv', uv1);
    }

    for (const resolver of this.resolvers) {
      const resolved = resolver(mesh, mesh.name);
      if (resolved) {
        mesh.material = resolved;
        this.register(`${mesh.name}:resolved`, resolved);
        return;
      }
    }

    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) if (material) this.normalize(material);
  }

  isEmissive(material: THREE.Material): boolean {
    const std = material as THREE.MeshStandardMaterial;
    const hasColor = !!std.emissive && std.emissive.r + std.emissive.g + std.emissive.b > 0;
    return hasColor || !!std.emissiveMap;
  }

  dispose(): void {
    this.registry.forEach((material) => material.dispose());
    this.registry.clear();
    this.resolvers.length = 0;
  }
}
